/**
 * Bot progress calculation utilities
 * Based on the Backbone bot_controls.js implementation
 */
import {
  BotCoefficients,
  ProgressCalculator,
  DEFAULT_COEFFICIENTS,
  CalculateTotalActionsOptions,
} from '../../models/bot';
import { IBotData, IBotSection } from '../../models/bot';

export type {
  BotCoefficients,
  ProgressCalculator,
  CalculateTotalActionsOptions,
};
export { DEFAULT_COEFFICIENTS };

/**
 * Calculates the total number of actions needed based on tunnels, sections, and accessories
 * This mirrors the calculateTime() function from the old Backbone implementation
 * Now properly handles multiple tunnels
 */
export const calculateTotalActions = (
  botData: IBotData,
  coefficients: BotCoefficients = DEFAULT_COEFFICIENTS,
  options: CalculateTotalActionsOptions = {},
): number => {
  const { compiled = false } = options;

  let totalActions = 0;

  // Create AHU
  totalActions += coefficients.create_ahu || 0;

  const tunnels = botData.tunnels || [];

  // Collect all valid sections across all tunnels first (for sizing check)
  const allValidSections: IBotSection[] = [];
  tunnels.forEach((tunnel) => {
    tunnel.sections?.forEach((section) => {
      if (section.type) {
        allValidSections.push(section);
      }
    });
  });

  totalActions += coefficients.size || 0;

  // Process each tunnel separately
  tunnels.forEach((tunnel, tunnelIndex) => {
    totalActions += coefficients.update_tunnel || 0;
    totalActions += coefficients.update_tunnel || 0;

    // Process each section in this tunnel
    tunnel.sections.forEach((section, sectionIdx) => {
      const prevSection =
        sectionIdx > 0 ? tunnel.sections[sectionIdx - 1] : null;

      // Base section operations
      totalActions += coefficients.create_section || 0;
      totalActions += coefficients.update_section || 0;

      // Section type-specific operations (skip if fan with tunnel_fan)
      if (
        section.type !== 'plenum' &&
        !(tunnelIndex > 1 && section.type === 'er_wheel')
      ) {
        const typeRateKey = `${section.type}_rate` as keyof BotCoefficients;
        const typeCreateKey = `${section.type}_create` as keyof BotCoefficients;

        if (typeRateKey in coefficients) {
          totalActions += (coefficients[typeRateKey] as number) || 0;
        }
        if (typeCreateKey in coefficients) {
          totalActions += (coefficients[typeCreateKey] as number) || 0;
        }
      }

      // Fan-specific operations
      if (section.type === 'fan') {
        totalActions += coefficients.update_section || 0;
        if (!compiled) {
          totalActions += coefficients.compile_errors || 0;
        }

        // Add section and access door if fan follows filter or coil with duty_id == 2
        // Only check previous section within the same tunnel
        if (
          prevSection &&
          (prevSection.type === 'filter' ||
            prevSection.type === 'er_wheel' ||
            (prevSection.type === 'coil' && prevSection.duty_id === 2))
        ) {
          totalActions += coefficients.create_section || 0;
        }
      }
      section.accessories.forEach((a) => {
        totalActions +=
          coefficients[a.type as 'access_door' | 'drain_pan' | 'opening'] || 0;
      });
    });
  });
  // Add compile errors for raised floor construction (once per AHU)
  //if (botData.floor_construction_id === 2) {
    totalActions += coefficients.compile_errors || 0;
  //}

  if (botData.tunnels[1]?.tunnel_index === '1:2') {
    totalActions += coefficients.increase_tunnel_width;
  }
  if (!botData.tunnels_have_same_directions) {
    totalActions += coefficients.increase_tunnel_length || 0;
  }
  // Tunnel floor casing update (one total, not per tunnel - matches old Backbone behavior)
  totalActions += coefficients.update_tunnel_floor_casing || 0;

  // AHU update (once per AHU)
  totalActions += coefficients.update_ahu || 0;

  return totalActions;
};

/**
 * Creates a progress calculator instance
 * @param onProgressChange - Optional callback to notify when progress changes
 * @param coefficients - Optional custom coefficients (defaults to DEFAULT_COEFFICIENTS)
 * @returns ProgressCalculator instance
 */
export const createProgressCalculator = (
  onProgressChange?: (percent: number) => void,
  coefficients: BotCoefficients = DEFAULT_COEFFICIENTS,
): ProgressCalculator => {
  let completedProcesses = 0;
  let percentCoefficient = 0;
  let allProcesses = 0;

  const calculateCompletedPercent = (
    process: number = 0,
    finished?: number,
  ): number => {
    if (finished !== undefined) {
      // If finished is provided, set percentage directly
      const percent = Math.min(100, Math.max(0, finished));
      onProgressChange?.(percent);
      return percent;
    }

    // Increment completed processes
    completedProcesses += process;

    // Calculate percentage (capped at 99% until explicitly set to 100%)
    const completed = Math.min(
      99,
      Math.ceil(completedProcesses * percentCoefficient),
    );
    onProgressChange?.(completed);
    return completed;
  };

  const reset = () => {
    completedProcesses = 0;
    percentCoefficient = 0;
    allProcesses = 0;
    onProgressChange?.(0);
  };

  /**
   * Initialize the calculator with total actions calculated from botData
   * This should be called before starting the design process
   */
  const initialize = (
    botData: IBotData,
    options: CalculateTotalActionsOptions = {},
  ) => {
    const totalActions = calculateTotalActions(botData, coefficients, options);
    allProcesses = totalActions;
    completedProcesses = 0;
    percentCoefficient = totalActions > 0 ? 99 / totalActions : 0;
    onProgressChange?.(0);
  };

  return {
    get completedProcesses() {
      return completedProcesses;
    },
    get percentCoefficient() {
      return percentCoefficient;
    },
    set percentCoefficient(value: number) {
      percentCoefficient = value;
    },
    get allProcesses() {
      return allProcesses;
    },
    set allProcesses(value: number) {
      allProcesses = value;
    },
    calculateCompletedPercent,
    reset,
    initialize,
  };
};

/**
 * Hook version for React components
 * Returns progress percentage and calculator methods
 */
export const useProgressCalculator = (
  coefficients: BotCoefficients = DEFAULT_COEFFICIENTS,
) => {
  const calculator = createProgressCalculator(
    undefined, // Progress will be managed via state
    coefficients,
  );

  return {
    calculator,
    coefficients,
  };
};
